<template>
    <div class="member-list">
      <div class="page-header">
        <div>
          <h2 class="page-title">会员管理</h2>
          <p class="page-desc">查看和管理所有会员信息</p>
        </div>
      </div>
      
      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-value">{{ stats.total || 0 }}</div>
          <div class="stat-label">总会员</div>
        </div>
        <div class="stat-card success">
          <div class="stat-value">{{ stats.active || 0 }}</div>
          <div class="stat-label">活跃会员</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-value">{{ stats.frozen || 0 }}</div>
          <div class="stat-label">已冻结</div>
        </div>
        <div class="stat-card primary">
          <div class="stat-value">{{ stats.total_points || 0 }}</div>
          <div class="stat-label">总积分</div>
        </div>
      </div>
      
      <!-- 搜索筛选栏 -->
      <div class="filter-bar">
        <el-input 
          v-model="searchKey" 
          placeholder="搜索昵称/手机号/OpenID"
          prefix-icon="Search"
          clearable
          style="width: 260px"
          @keyup.enter="handleSearch"
        />
        <el-select v-model="levelFilter" placeholder="全部等级" clearable style="width: 130px" @change="handleSearch">
          <el-option v-for="level in levels" :key="level.level_code" :label="level.level_name" :value="level.level_code" />
        </el-select>
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="handleSearch">
          <el-option label="正常" :value="1" />
          <el-option label="冻结" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
      
      <!-- 会员表格 -->
      <el-table :data="memberList" border v-loading="loading" style="width: 100%">
        <el-table-column label="会员信息" min-width="220">
          <template #default="{ row }">
            <div class="member-info">
              <el-avatar :size="40" :src="row.avatar_url || defaultAvatar" />
              <div class="member-detail">
                <div class="nickname">{{ row.nickname || '匿名用户' }}</div>
                <div class="phone">{{ row.phone || '未绑定' }}</div>
                <div class="openid">OpenID: {{ row.openid?.substring(0, 15) }}...</div>
              </div>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="会员等级" width="100" align="center">
          <template #default="{ row }">
            <div class="level-badge" :class="row.member_level_code">
              {{ getLevelName(row.member_level_code) }}
            </div>
          </template>
        </el-table-column>
        
        <el-table-column prop="points" label="积分" width="100" align="center">
          <template #default="{ row }">
            <span class="points-value">{{ row.points || 0 }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="邀请码" width="120" align="center">
          <template #default="{ row }">
            <div class="invite-code">
              <span>{{ row.invite_code || '--' }}</span>
              <el-button v-if="row.invite_code" link size="small" @click="copyCode(row.invite_code)">
                <el-icon><CopyDocument /></el-icon>
              </el-button>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="注册时间" width="160" align="center">
          <template #default="{ row }">
            {{ formatTime(row.create_time) }}
          </template>
        </el-table-column>
        
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '正常' : '冻结' }}
            </el-tag>
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" link size="small" @click="viewDetail(row)">
              详情
            </el-button>
            <el-button 
              :type="row.status === 1 ? 'warning' : 'success'" 
              link 
              size="small" 
              @click="toggleStatus(row)"
            >
              {{ row.status === 1 ? '冻结' : '解冻' }}
            </el-button>
            <el-button type="primary" link size="small" @click="adjustPoints(row)">
              调整积分
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.limit"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchData"
          @current-change="fetchData"
        />
      </div>
      
      <!-- 会员详情弹窗 -->
      <el-dialog v-model="detailDialogVisible" title="会员详情" width="700px">
        <div v-if="currentMember" class="member-detail">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="会员ID">{{ currentMember.id }}</el-descriptions-item>
            <el-descriptions-item label="OpenID">{{ currentMember.openid }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{ currentMember.nickname || '--' }}</el-descriptions-item>
            <el-descriptions-item label="手机号">{{ currentMember.phone || '--' }}</el-descriptions-item>
            <el-descriptions-item label="会员等级">{{ getLevelName(currentMember.member_level_code) }}</el-descriptions-item>
            <el-descriptions-item label="当前积分">{{ currentMember.points || 0 }}</el-descriptions-item>
            <el-descriptions-item label="邀请码">{{ currentMember.invite_code || '--' }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag :type="currentMember.status === 1 ? 'success' : 'danger'" size="small">
                {{ currentMember.status === 1 ? '正常' : '冻结' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="注册时间">{{ formatTime(currentMember.create_time) }}</el-descriptions-item>
            <el-descriptions-item label="最后登录">{{ formatTime(currentMember.last_login_time) || '--' }}</el-descriptions-item>
          </el-descriptions>
          
          <div class="points-log-section">
            <h4>积分记录</h4>
            <el-table :data="pointsLog" size="small" v-loading="pointsLogLoading">
              <el-table-column prop="points_change" label="变动积分" width="100">
                <template #default="{ row }">
                  <span :class="row.points_change > 0 ? 'points-plus' : 'points-minus'">
                    {{ row.points_change > 0 ? '+' : '' }}{{ row.points_change }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="points_before" label="变动前" width="80" align="center" />
              <el-table-column prop="points_after" label="变动后" width="80" align="center" />
              <el-table-column prop="type" label="类型" width="100">
                <template #default="{ row }">
                  {{ getPointsTypeText(row.type) }}
                </template>
              </el-table-column>
              <el-table-column prop="source" label="来源" min-width="150" />
              <el-table-column prop="create_time" label="时间" width="160">
                <template #default="{ row }">{{ formatTime(row.create_time) }}</template>
              </el-table-column>
            </el-table>
            <div class="pagination-small" v-if="pointsLogPagination.total > pointsLogPagination.limit">
              <el-pagination
                v-model:current-page="pointsLogPagination.page"
                v-model:page-size="pointsLogPagination.limit"
                :total="pointsLogPagination.total"
                layout="prev, pager, next"
                small
                @current-change="loadPointsLog"
              />
            </div>
          </div>
        </div>
      </el-dialog>
      
      <!-- 调整积分弹窗 -->
      <el-dialog v-model="pointsDialogVisible" title="调整积分" width="400px">
        <el-form :model="pointsForm" label-width="80px">
          <el-form-item label="当前积分">
            <span class="current-points">{{ currentMember?.points || 0 }}</span>
          </el-form-item>
          <el-form-item label="调整类型" required>
            <el-radio-group v-model="pointsForm.type">
              <el-radio :label="1">增加积分</el-radio>
              <el-radio :label="0">减少积分</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="调整数量" required>
            <el-input-number v-model="pointsForm.amount" :min="1" :max="10000" />
          </el-form-item>
          <el-form-item label="调整原因">
            <el-input v-model="pointsForm.remark" placeholder="请输入调整原因" />
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="pointsDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmAdjustPoints" :loading="pointsAdjustLoading">确 定</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, onMounted } from 'vue'
  import { ElMessage} from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { CopyDocument } from '@element-plus/icons-vue'
  
  export default {
    name: 'MemberListPage',
    components: { CopyDocument },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
      
      const loading = ref(false)
      const searchKey = ref('')
      const levelFilter = ref(null)
      const statusFilter = ref(null)
      const memberList = ref([])
      const levels = ref([])
      const stats = ref({})
      const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      
      const detailDialogVisible = ref(false)
      const currentMember = ref(null)
      const pointsLog = ref([])
      const pointsLogLoading = ref(false)
      const pointsLogPagination = reactive({ page: 1, limit: 10, total: 0 })
      
      const pointsDialogVisible = ref(false)
      const pointsAdjustLoading = ref(false)
      const pointsForm = reactive({ type: 1, amount: 0, remark: '' })
      
      const pagination = reactive({
        page: 1,
        limit: 10,
        total: 0
      })
      
      // 获取等级名称
      const getLevelName = (code) => {
        const level = levels.value.find(l => l.level_code === code)
        return level ? level.level_name : '普通会员'
      }
      
      // 获取积分类型文本
      const getPointsTypeText = (type) => {
        const map = { consume: '消费', reward: '奖励', deduct: '抵扣', expire: '过期', activity: '活动' }
        return map[type] || type
      }
      
      // 格式化时间
      const formatTime = (time) => {
        if (!time) return '-'
        return new Date(time).toLocaleString('zh-CN')
      }
      
      // 复制
      const copyCode = async (code) => {
        try {
          await navigator.clipboard.writeText(code)
          ElMessage.success('已复制')
        } catch {
          ElMessage.error('复制失败')
        }
      }
      
      // 获取会员列表
      const fetchData = async () => {
        loading.value = true
        try {
          const params = {
            action: 'list',
            agent_id: agentId.value,
            store_id: storeId.value,
            page: pagination.page,
            limit: pagination.limit,
            search: searchKey.value,
            level_code: levelFilter.value,
            status: statusFilter.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, { params })
          
          if (response.data.code === 200) {
            memberList.value = response.data.data.list
            stats.value = response.data.data.stats
            pagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取会员列表失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          loading.value = false
        }
      }
      
      // 获取会员等级
      const fetchLevels = async () => {
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, {
            params: { action: 'levels', agent_id: agentId.value, store_id: storeId.value }
          })
          if (response.data.code === 200) {
            levels.value = response.data.data
          }
        } catch (error) {
          console.error('获取等级失败:', error)
        }
      }
      
      // 加载积分记录
      const loadPointsLog = async () => {
        if (!currentMember.value) return
        
        pointsLogLoading.value = true
        try {
          const response = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, {
            params: {
              action: 'points_log',
              user_id: currentMember.value.id,
              store_id: storeId.value,
              page: pointsLogPagination.page,
              limit: pointsLogPagination.limit
            }
          })
          
          if (response.data.code === 200) {
            pointsLog.value = response.data.data.list
            pointsLogPagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取积分记录失败:', error)
        } finally {
          pointsLogLoading.value = false
        }
      }
      
      // 搜索
      const handleSearch = () => {
        pagination.page = 1
        fetchData()
      }
      
      // 重置搜索
      const resetSearch = () => {
        searchKey.value = ''
        levelFilter.value = null
        statusFilter.value = null
        pagination.page = 1
        fetchData()
      }
      
      // 查看详情
      const viewDetail = async (row) => {
        currentMember.value = row
        pointsLogPagination.page = 1
        await loadPointsLog()
        detailDialogVisible.value = true
      }
      
      // 切换状态
      const toggleStatus = async (row) => {
        const newStatus = row.status === 1 ? 0 : 1
        const action = newStatus === 1 ? '解冻' : '冻结'
        
        try {
          const response = await axios.put(`${config.BASE_URL}/shop/api/member_api.php`, {
            id: row.id,
            store_id: storeId.value,
            status: newStatus
          })
          
          if (response.data.code === 200) {
            ElMessage.success(`${action}成功`)
            fetchData()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('操作失败:', error)
          ElMessage.error('操作失败')
        }
      }
      
      // 调整积分
      const adjustPoints = (row) => {
        currentMember.value = row
        pointsForm.type = 1
        pointsForm.amount = 0
        pointsForm.remark = ''
        pointsDialogVisible.value = true
      }
      
      // 确认调整积分
      const confirmAdjustPoints = async () => {
        if (pointsForm.amount <= 0) {
          ElMessage.warning('请输入调整数量')
          return
        }
        
        pointsAdjustLoading.value = true
        try {
          const changeAmount = pointsForm.type === 1 ? pointsForm.amount : -pointsForm.amount
          const newPoints = currentMember.value.points + changeAmount
          
          if (newPoints < 0) {
            ElMessage.warning('积分不能为负数')
            return
          }
          
          const response = await axios.put(`${config.BASE_URL}/shop/api/member_api.php`, {
            id: currentMember.value.id,
            store_id: storeId.value,
            points: newPoints
          })
          
          if (response.data.code === 200) {
            ElMessage.success('积分调整成功')
            pointsDialogVisible.value = false
            fetchData()
            if (detailDialogVisible.value) {
              currentMember.value.points = newPoints
              await loadPointsLog()
            }
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('调整积分失败:', error)
          ElMessage.error('操作失败')
        } finally {
          pointsAdjustLoading.value = false
        }
      }
      
      onMounted(() => {
        fetchLevels()
        fetchData()
      })
      
      return {
        loading,
        searchKey,
        levelFilter,
        statusFilter,
        memberList,
        levels,
        stats,
        pagination,
        defaultAvatar,
        detailDialogVisible,
        currentMember,
        pointsLog,
        pointsLogLoading,
        pointsLogPagination,
        pointsDialogVisible,
        pointsAdjustLoading,
        pointsForm,
        getLevelName,
        getPointsTypeText,
        formatTime,
        copyCode,
        handleSearch,
        resetSearch,
        viewDetail,
        toggleStatus,
        adjustPoints,
        confirmAdjustPoints,
        loadPointsLog,
        fetchData
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .member-list {
    .page-header {
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .stat-cards {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      flex-wrap: wrap;
      
      .stat-card {
        flex: 1;
        min-width: 100px;
        background: #fff;
        border-radius: 12px;
        padding: 16px 20px;
        text-align: center;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        
        &.success .stat-value { color: #67c23a; }
        &.warning .stat-value { color: #e6a23c; }
        &.primary .stat-value { color: #409eff; }
        
        .stat-value {
          font-size: 28px;
          font-weight: 700;
        }
        
        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
      }
    }
    
    .filter-bar {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
    
    .member-info {
      display: flex;
      gap: 12px;
      align-items: center;
      
      .member-detail {
        .nickname {
          font-weight: 500;
          margin-bottom: 2px;
        }
        
        .phone, .openid {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }
    
    .level-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      
      &.bronze { background: #f5e6d3; color: #b8860b; }
      &.silver { background: #e8e8e8; color: #8c8c8c; }
      &.gold { background: #fff0d4; color: #d4a017; }
      &.platinum { background: #e6f0ff; color: #409eff; }
    }
    
    .points-value {
      font-weight: 600;
      color: #e6a23c;
    }
    
    .invite-code {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
    }
    
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    }
    
    .member-detail {
      .points-log-section {
        margin-top: 20px;
        
        h4 {
          margin-bottom: 12px;
          font-size: 14px;
          font-weight: 600;
        }
        
        .points-plus { color: #67c23a; font-weight: 500; }
        .points-minus { color: #f56c6c; font-weight: 500; }
      }
    }
    
    .pagination-small {
      display: flex;
      justify-content: flex-end;
      margin-top: 12px;
    }
    
    .current-points {
      font-size: 18px;
      font-weight: 600;
      color: #e6a23c;
    }
  }
  </style>